<template>
  <div>
    <span class="text-caption grey--text text--lighten-1">
      This application is not intended to be used as a medical device by the
      manufacturer.
      {{ validationOutcome }}
    </span>
    <v-container class="d-flex align-center pa-0">
      <div class="my-3 ml-3 mr-13">
        <v-fade-transition>
          <div v-if="!transition" class="d-flex flex-column align-center">
            <v-progress-circular
              :color="validationOutcomeColor"
              size="156"
              :rotate="270"
              :value="progress"
              class="progress-circular-transition"
            >
              <span
                :class="[
                  `${validationOutcomeColor}--text`,
                  'title-font',
                  'text-center'
                ]"
                style="font-size: x-large"
              >
                {{ validationOutcome }}
              </span>
            </v-progress-circular>
          </div>
        </v-fade-transition>
      </div>
      <div>
        <v-row align="center" class="mt-0">
          <v-btn
            @click="onScanColorsButtonClick"
            :color="validationOutcomeColor"
            class="text-body-2 elevation-0 mt-5 mr-8 rounded-10"
          >
            {{ toggleScanColorsButtonLabel }}
          </v-btn>
          <v-checkbox
            v-model="showScanTissue"
            label="Show Scan Tissue"
            hide-details
          />
        </v-row>
        <v-row style="min-height: 90px">
          <v-col v-if="showHeatmapColors">
            <HeatmapScale class="ml-5" />
          </v-col>
          <v-col v-if="validationOutcome === 'Consider Rescan'" cols="12">
            <v-card-text class="text-left pa-0">
              <h3 class="mb-2 title-font">Consider Rescanning</h3>
              <p>
                The Scan Validation process has determined that the scans
                provided may produce inaccurate results. This is a warning only,
                the case may continue at your discretion.
              </p>
            </v-card-text>
          </v-col>
        </v-row>
      </div>
    </v-container>
    <div
      v-if="uploadedScans.length === 3 && validationOutcome === 'Fail'"
      class="mt-5 mb-n2"
    >
      &nbsp;<v-icon color="error" class="mr-1">mdi-alert-circle</v-icon>
      Maximum retry count reached.
    </div>
    <v-row class="mt-0">
      <v-col>
        <v-btn
          v-if="validationOutcome !== 'Fail'"
          class="btn-primary mr-3"
          @click="$emit('next')"
        >
          Continue
        </v-btn>
        <template v-if="validationOutcome !== 'Pass'">
          <v-btn
            :disabled="!retryPermitted"
            @click="onRetry"
            class="my-3 mx-2 btn-primary"
          >
            Retry With New Scans
          </v-btn>
          <v-btn class="elevation-0 rounded-10" @click="restart">
            Restart Session
          </v-btn>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<style>
.progress-circular-transition .v-progress-circular__overlay {
  transition: all 0.6s ease-in-out !important;
}
</style>

<script>
import { mapActions, mapGetters } from "vuex"
import { toTitleCase } from "@/utils"
import * as NCFrontend from "@osteon-nexus-connect-lib/nc-frontend"
import HeatmapScale from "./PostAlignment/HeatmapScale.vue"
import heatmapImg from "@/assets/image-heatmap-scale-light.svg"

export default {
  name: "PostAlignment",
  components: {
    HeatmapScale
  },
  props: ["dentate"],

  data() {
    return {
      heatmapImg,
      progress: 0,
      showHeatmapColors: false,
      showScanTissue: true
    }
  },

  mounted() {
    setTimeout(() => (this.progress = 100), 500)
  },

  watch: {
    showScanTissue(value) {
      NCFrontend.get("scanValidationView").visualisation.setScanTissueVisible(
        value
      )
    }
  },

  computed: {
    ...mapGetters([
      "upperValidationOutcome",
      "lowerValidationOutcome",
      "upperUploadedScans",
      "lowerUploadedScans",
      "transition",
      "retryCount"
    ]),

    uploadedScans() {
      return this[`${this.dentate}UploadedScans`]
    },

    toggleScanColorsButtonLabel() {
      if (!this.showHeatmapColors) {
        return "Show Heatmap Colors"
      }
      return "Show Score Colors"
    },

    retryPermitted() {
      return this.uploadedScans.length < 3
    },

    validationOutcome() {
      return this[`${this.dentate}ValidationOutcome`]
    },

    validationOutcomeColor() {
      switch (this[`${this.dentate}ValidationOutcome`]) {
        case "Fail":
          return "error"
        case "Consider Rescan":
          return "warning"
        default:
          return "success"
      }
    }
  },
  methods: {
    ...mapActions([
      "setUpperValidateScansResponse",
      "setLowerValidateScansResponse",
      "setRetryCount",
      "setPageState",
      "setRetrying",
      "resetForDentate"
    ]),

    onScanColorsButtonClick() {
      if (!this.showHeatmapColors) {
        NCFrontend.get("scanValidationView").visualisation.showHeatmapColors()
        this.showHeatmapColors = true
      } else {
        NCFrontend.get("scanValidationView").visualisation.showScoreColors()
        this.showHeatmapColors = false
      }
    },

    restart() {
      NCFrontend.get("scan1View").visualisation.reset()
      NCFrontend.get("scan2View").visualisation.reset()
      NCFrontend.remove(
        NCFrontend.get("scan1View"),
        NCFrontend.get("scan2View")
      )
      this.resetForDentate(this.dentate, this.$vuetify.breakpoint.lgAndUp)
      this.$emit("switch")
      this.$emit("step", "UploadScans", this.dentate)
    },

    onRetry() {
      NCFrontend.get("scan1View").clearVisualisation()
      NCFrontend.get("scan2View").clearVisualisation()
      NCFrontend.get("scanValidationView").clearVisualisation()
      this[`set${toTitleCase(this.dentate)}ValidateScansResponse`](null)
      this.setRetryCount(this.retryCount + 1)
      this.setRetrying(this.dentate)

      this.setPageState([
        {
          cols: this.$vuetify.breakpoint.lgAndUp ? 7 : 5,
          component: "ConnectBranding"
        }
      ])
      this.$emit("switch")

      this.$emit("step", "UploadScans")
    }
  }
}
</script>
